import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { environment } from '@env/environment';

export interface NextCourse {
  course_id: number;
  title: string;
  description?: string;
  module?: string;
  sort_order?: number;
  is_mandatory?: boolean;
  due_by_days?: number;
  is_nachschulung?: boolean;
}

export interface StartNextCourseResult {
  student_course_id: number;
  course_id?: number;
  course_title?: string;
  already_enrolled: boolean;
}

export interface RatingResult {
  student_course_id: number;
  rating_tier: number;
  nachschulung_triggered: boolean;
  full_retake: boolean;
  auto_trigger_threshold: number;
  retake_threshold: number;
}

export interface CompanyConfig {
  key: string;
  value: string;
  company_id: number;
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*' }),
};

@Injectable({ providedIn: 'root' })
export class CompletionFlowService {
  constructor(private httpClient: HttpClient) {}

  getNextCourse(studentId: number): Observable<NextCourse | null> {
    return this.httpClient
      .get<any>(`${environment.serverUrl}/v1/students/${studentId}/next-course`, httpOptions)
      .pipe(map(r => r.data || null), catchError(this.handleError));
  }

  startNextCourse(studentId: number, courseId?: number): Observable<StartNextCourseResult> {
    const body = courseId ? { course_id: courseId } : {};
    return this.httpClient
      .post<any>(`${environment.serverUrl}/v1/students/${studentId}/start-next-course`, JSON.stringify(body), httpOptions)
      .pipe(map(r => r.data), catchError(this.handleError));
  }

  rateStudentCourse(studentCourseId: number, ratingTier: number): Observable<RatingResult> {
    return this.httpClient
      .post<any>(`${environment.serverUrl}/v1/student-courses/${studentCourseId}/rate`, JSON.stringify({ rating_tier: ratingTier }), httpOptions)
      .pipe(map(r => r.data), catchError(this.handleError));
  }

  getCompanyConfig(key: string): Observable<CompanyConfig> {
    return this.httpClient
      .get<any>(`${environment.serverUrl}/v1/company-config/${key}`, httpOptions)
      .pipe(map(r => r.data), catchError(this.handleError));
  }

  setCompanyConfig(key: string, value: string): Observable<CompanyConfig> {
    return this.httpClient
      .put<any>(`${environment.serverUrl}/v1/company-config/${key}`, JSON.stringify({ value }), httpOptions)
      .pipe(map(r => r.data), catchError(this.handleError));
  }

  private handleError(error: any) {
    const msg = error?.error?.error || error?.message || 'Unknown error';
    return throwError(() => msg);
  }
}
